<template>
  <div class="users-table">
    <button-group />
    <div class="table-container">
      <table class="table is-fullwidth is-striped is-hoverable">
        <thead>
          <tr>
            <th colspan="2">ID</th>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Created</th>
            <th>Last Login</th>
            <th>Login</th>
          </tr>
        </thead>
        <!-- Skeleton Loading State -->
        <transition name="fade" mode="out-in">
          <tbody v-if="isLoadingSync" key="loading" class="skeleton-tbody">
            <tr
              v-for="n in skeletonRows"
              :key="`skeleton-${n}`"
              class="skeleton-row"
            >
              <td colspan="2">
                <div class="skeleton skeleton-text"></div>
              </td>
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-text skeleton-tag"></div></td>
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-text"></div></td>
              <td><div class="skeleton skeleton-button"></div></td>
            </tr>
          </tbody>

          <!-- Actual Data -->
          <tbody v-else-if="users.length > 0" key="data" class="data-tbody">
            <tr v-for="(user, i) in users" :key="i">
              <td v-if="user.is_active">
                <span class="tag is-small is-success">A</span>
              </td>
              <td v-else><span class="tag is-small is-danger">P</span></td>
              <td>{{ user.id }}</td>
              <td>
                <a
                  href="#"
                  @click="editUser(user)"
                  :title="user.full_name"
                  class="entity-link"
                  >{{ truncate(user.full_name, 20)
                  }}<span class="icon is-small edit-icon"
                    ><i class="fas fa-edit"></i></span
                ></a>
              </td>
              <td :title="user.email">{{ truncate(user.email, 20) }}</td>
              <td>
                <div v-if="user.roles && user.roles.length > 0" class="tags">
                  <span class="tag is-info"
                    >{{ user.roles[0]?.title }}
                    <span v-if="user.roles.length > 1"
                      >+{{ user.roles.length - 1 }}</span
                    ></span
                  >
                </div>
              </td>
              <td :title="formatDate(user.created_at, { showRelative: false })">
                {{ formatDate(user.created_at) }}
              </td>
              <td :title="formatDate(user.created_at, { showRelative: false })">
                {{ formatDate(user.last_login) }}
              </td>
              <td>
                <button class="button" @click="loginAsUser(user)">
                  <span class="icon">
                    <i class="fas fa-lock"></i>
                  </span>
                </button>
              </td>
            </tr>
          </tbody>
          <!-- Empty State -->
          <tbody v-else key="empty">
            <tr>
              <td colspan="9" class="has-text-centered empty-state">
                <div class="empty-state-content">
                  <p class="has-text-grey">
                    <span class="icon is-large">
                      <i class="fas fa-users fa-2x"></i>
                    </span>
                  </p>
                  <p class="has-text-grey">No users found</p>
                </div>
              </td>
            </tr>
          </tbody>
        </transition>
      </table>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import ButtonGroup from "@/components/ButtonGroup.vue";
import { formatDate } from "@/utils/date_converter";
export default {
  components: {
    "button-group": ButtonGroup,
  },
  data() {
    return {
      skeletonRows: 10, // Number of skeleton rows to show during loading
    };
  },
  computed: {
    ...mapState(["users", "isLoadingSync"]),
  },
  filters: {
    status: function (value) {
      return value === "1" ? "Yes" : "No";
    },
    capitalize: function (value) {
      if (!value) return "";
      value = value.toString();
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
  },

  methods: {
    ...mapActions(["fetchList", "openUserModal", "loginAsUser"]),
    editUser(user) {
      this.openUserModal(user);
    },
    truncate(text, length) {
      if (!text) return "";
      return text.length > length ? text.substring(0, length) + "..." : text;
    },
    formatDate,
  },

  mounted() {
    this.fetchList();
  },
};
</script>

<style scoped>
.table-container {
  position: relative;
  min-height: 400px;
  transition: min-height 0.3s ease;
}

/* Skeleton Loading Styles */
.skeleton-row {
  pointer-events: none;
}

.skeleton {
  background: linear-gradient(90deg, #f0f0f0 0%, #f8f8f8 50%, #f0f0f0 100%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  display: inline-block;
}

.skeleton-text {
  height: 20px;
  width: 80%;
  max-width: 120px;
}

.skeleton-tag {
  height: 24px;
  width: 60px;
  border-radius: 4px;
}

.skeleton-button {
  height: 32px;
  width: 80px;
  border-radius: 4px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* Vue Transition Classes */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

/* Empty state styling */
.empty-state {
  padding: 3rem !important;
}

.empty-state-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.empty-state-content .icon {
  color: #dbdbdb;
}

/* Entity link hover effects */
.entity-link {
  text-decoration: none;
  color: inherit;
  transition: color 0.2s ease;
  display: inline-flex;
  align-items: center;
}

.entity-link:hover {
  color: #3273dc;
}

.entity-link .edit-icon {
  opacity: 0;
  transform: translateX(-4px);
  transition: opacity 0.2s ease, transform 0.2s ease;
  margin-left: 0.5rem;
}

.entity-link:hover .edit-icon {
  opacity: 1;
  transform: translateX(0);
}
</style>
